/**
 * Modern SSE Hook with production-ready features:
 * - Durable delivery with resume capability
 * - Single connection per user across tabs
 * - Exponential backoff with jitter
 * - Event deduplication and versioning
 * - TypeScript event safety
 * - Connection state management
 * - BroadcastChannel for multi-tab sync
 */

import { apiConfig } from "@/config";
import { supabase } from "@/lib/supabase";
import type { StudiesListResponse, Study, StudyStatusPayload } from "@/types/api";
import { handleUnauthorizedRedirect } from "@/utils/authErrorHandler";
import { logger } from "@/utils/logger";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

// SSE Connection States
export type SSEConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "tab_inactive";

// Structured SSE Event Types
export interface SSEEvent {
  id: string;
  event: string;
  study_id?: string;
  user_id: string;
  version: number;
  timestamp: string;
  data: Record<string, unknown>;
}

/** Check whether an event is a server heartbeat (carries no debugging data). */
export function isHeartbeatEvent(event: SSEEvent): boolean {
  return event.event === "heartbeat" || event.id === "heartbeat";
}

// Connection Configuration
interface SSEConfig {
  enabled: boolean;
  baseRetryDelay: number;
  maxRetryDelay: number;
  heartbeatTimeout: number;
  eventBufferSize: number;
}

// Default configuration
const DEFAULT_CONFIG: SSEConfig = {
  enabled: true,
  baseRetryDelay: 1000, // 1 second
  maxRetryDelay: 30000, // 30 seconds
  heartbeatTimeout: 45000, // 45 seconds (30s + 15s buffer)
  eventBufferSize: 100,
};

// Connection Manager for single connection across tabs
export class SSEConnectionManager {
  private static instance: SSEConnectionManager;
  private abortController: AbortController | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private isLeaderTab = false;
  private leaderPresent = false;
  private leaderElectionTimer: number | null = null;
  private authToken?: string;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private tokenRefreshTimer: number | null = null;
  private lastEventId: string | null = null;
  private config: SSEConfig = DEFAULT_CONFIG;
  private listeners: Set<(event: SSEEvent) => void> = new Set();
  private stateListeners: Set<(state: SSEConnectionState) => void> = new Set();
  private currentState: SSEConnectionState = "disconnected";
  private eventBuffer: SSEEvent[] = [];
  private processedEventIds: Set<string> = new Set();

  private constructor() {
    this.initializeBroadcastChannel();
    // Use robust navigator.locks for leader election
    this.requestLeaderLock();
  }

  public static getInstance(): SSEConnectionManager {
    if (!SSEConnectionManager.instance) {
      SSEConnectionManager.instance = new SSEConnectionManager();
    }
    return SSEConnectionManager.instance;
  }

  private initializeBroadcastChannel() {
    if (typeof BroadcastChannel !== "undefined") {
      this.broadcastChannel = new BroadcastChannel("sse-events");

      this.broadcastChannel.addEventListener("message", (event) => {
        const { type, data, state } = event.data || {};

        switch (type) {
          case "sse_event":
            this.handleEventFromBroadcast(data);
            break;
          case "state_change":
            // Followers learn leader state
            if (!this.isLeaderTab && state) {
              this.currentState = state;
              this.stateListeners.forEach((l) => l(state));
            }
            break;
          case "state_request":
            if (this.isLeaderTab) {
              this.broadcastState();
            }
            break;
          // No need for leader_ping/response with navigator.locks
        }
      });
    }
  }

  private async requestLeaderLock() {
    if (typeof navigator.locks === "undefined") {
      // Fallback for very old browsers (unlikely given project stack)
      logger.warn("navigator.locks not available, falling back to simple leader election");
      this.electLeaderTabLegacy();
      return;
    }

    navigator.locks.request("sse_leader_lock", { ifAvailable: true }, async (lock) => {
      if (!lock) {
        // Lock not acquired - we are a follower
        this.isLeaderTab = false;
        logger.debug("👀 SSE Follower Tab active");
        this.requestLeaderState();
        return;
      }

      // Lock acquired - we are the leader
      this.isLeaderTab = true;
      logger.info("🏆 Elected as SSE leader tab (via navigator.locks)");
      
      // Connect if we have a token or config is enabled
      if (this.config.enabled) {
        this.connect(this.authToken);
      }

      // Hold the lock indefinitely until tab closes or refreshes
      return new Promise<void>(() => {});
    });
  }

  private electLeaderTabLegacy(force = false) {
    if (!this.broadcastChannel) {
      this.isLeaderTab = true;
      this.connect();
      return;
    }

    this.leaderPresent = false;
    this.broadcastChannel.postMessage({ type: "leader_ping" });

    if (this.leaderElectionTimer) clearTimeout(this.leaderElectionTimer);
    this.leaderElectionTimer = window.setTimeout(() => {
      if (!this.isLeaderTab && (!this.leaderPresent || force)) {
        this.isLeaderTab = true;
        this.setState("connecting");
        this.connect(this.authToken);
        logger.info("🏆 Elected as SSE leader tab (Legacy)");
      }
    }, 500);
  }

  private setState(state: SSEConnectionState) {
    if (this.currentState !== state) {
      this.currentState = state;
      this.stateListeners.forEach((listener) => listener(state));

      // Broadcast state to other tabs
      this.broadcastState();
    }
  }

  private broadcastState() {
    if (this.broadcastChannel && this.isLeaderTab) {
      this.broadcastChannel.postMessage({
        type: "state_change",
        state: this.currentState,
        lastEventId: this.lastEventId,
      });
    }
  }

  private requestLeaderState() {
    if (this.broadcastChannel && !this.isLeaderTab) {
      this.broadcastChannel.postMessage({ type: "state_request" });
    }
  }

  private async getFreshToken(): Promise<string | null> {
    try {
      if (!supabase) {
        logger.warn("Supabase client not initialized for SSE token refresh");
        return this.authToken ?? null;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        logger.warn("Error getting Supabase session for SSE token refresh:", error);
        return null;
      }

      // Check if token is expired or will expire soon (within 5 minutes)
      if (session?.access_token && session?.expires_at) {
        const expiresAtMs = session.expires_at * 1000;
        const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
        
        if (expiresAtMs < fiveMinutesFromNow) {
          logger.info("SSE token expiring soon, refreshing...", {
            expiresAt: new Date(expiresAtMs).toISOString(),
            timeUntilExpiry: Math.round((expiresAtMs - Date.now()) / 1000) + "s"
          });
          
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            logger.warn("Failed to refresh SSE token:", refreshError);
            return session.access_token; // Return current token as fallback
          }
          
          if (refreshData?.session?.access_token) {
            logger.info("SSE token refreshed successfully");
            return refreshData.session.access_token;
          }
        }
      }

      return session?.access_token ?? this.authToken ?? null;
    } catch (err) {
      logger.warn("Failed to get fresh auth token for SSE:", err);
      return null;
    }
  }

  public setAuthToken(token?: string) {
    if (typeof token === "string") {
      this.authToken = token;
    }
  }

  public connect(token?: string) {
    // Persist auth token for reconnects
    if (token) this.authToken = token;

    if (!this.isLeaderTab || !this.config.enabled) {
      return;
    }

    this.clearTimers();

    if (this.abortController) {
      this.abortController.abort();
    }

    // Build the correct SSE URL regardless of how apiConfig.baseUrl is shaped.
    // If baseUrl is absolute and contains a path like "/api", using
    // new URL("/v1...", baseUrl) would DROP the "/api" segment.
    // Normalize by concatenating strings instead of relying on URL resolution.
    const ssePath = "v1/events/stream"; // intentionally no leading slash
    const base = apiConfig.baseUrl || "/api";
    const baseNormalized = base.replace(/\/$/, ""); // strip trailing slash
    const fullPath = `${baseNormalized}/${ssePath}`; // preserves /api when present
    const finalUrl = base.startsWith("http")
      ? new URL(fullPath)
      : new URL(fullPath, window.location.origin);

    try {
      this.abortController = new AbortController();
      this.setState("connecting");

      // Use fetch-event-source for proper header support
      fetchEventSource(finalUrl.toString(), {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          // Use persisted Authorization token
          ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
          // Include Last-Event-ID for resume capability
          ...(this.lastEventId && { "Last-Event-ID": this.lastEventId }),
        },
        signal: this.abortController.signal,

        onopen: async (response) => {
          logger.info(`🔌 SSE Connecting to ${finalUrl.toString()}`, {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
          });

          if (response.status === 401) {
            logger.warn(
              "❌ SSE unauthorized response received - redirecting to login",
            );
            try {
              await handleUnauthorizedRedirect();
            } catch (redirectError) {
              logger.warn(
                "Failed to handle unauthorized redirect for SSE",
                redirectError,
              );
            }
            if (this.abortController) {
              this.abortController.abort();
            }
            throw new Error("Unauthorized SSE connection");
          }

          if (
            response.ok &&
            response.headers.get("content-type")?.includes("text/event-stream")
          ) {
            logger.info("✅ SSE connection established");
            this.setState("connected");
            this.startHeartbeatTimer();
            this.startTokenRefreshTimer();
          } else {
            // Don't log connection failures to Sentry - they're expected and frequent
            // Only log as warning for local debugging
            logger.warn(
              `❌ SSE connection failed: ${response.status} ${response.statusText}`,
            );
            throw new Error(`Failed to connect: ${response.status}`);
          }
        },

        onmessage: (ev) => {
          try {
            // Reset heartbeat timer on any event
            this.resetHeartbeatTimer();

            // ev is { data: string; id?: string; event?: string } from fetch-event-source
            const parsed = JSON.parse(ev.data);

            // Prefer protocol fields, but fall back to payload if needed
            const eventId =
              ev.id ?? parsed.id ?? `${Date.now()}-${Math.random()}`;
            const eventType =
              ev.event ?? parsed.event ?? parsed.event_type ?? "message";

            // Skip if we've already processed this event (deduplication)
            if (this.processedEventIds.has(eventId)) {
              return;
            }

            const sseEvent: SSEEvent = {
              id: eventId,
              event: eventType,
              study_id: parsed.study_id,
              user_id: parsed.user_id,
              version: parsed.version ?? 0,
              timestamp: parsed.timestamp ?? new Date().toISOString(),
              data: parsed, // keep the raw payload for consumers
            };

            this.processEvent(sseEvent);
          } catch (error) {
            logger.error("Failed to parse SSE event:", { error, rawData: ev.data });
          }
        },

        onclose: () => {
          // Do NOT schedule our own reconnect here; let the library decide
          logger.warn("🔌 SSE connection closed");
          this.setState("disconnected");
        },

        onerror: (error) => {
          const maybeStatus = (() => {
            if (typeof (error as { status?: number }).status === "number") {
              return (error as { status: number }).status;
            }

            const responseStatus = (error as { response?: Response }).response?.status;
            return typeof responseStatus === "number" ? responseStatus : undefined;
          })();

          const message = error instanceof Error ? error.message : String(error);

          if (maybeStatus === 401 || message.includes("401")) {
            logger.warn(
              "❌ SSE connection error due to unauthorized response - redirecting",
            );
            handleUnauthorizedRedirect().catch((redirectError) => {
              logger.warn(
                "Failed to handle unauthorized redirect after SSE error",
                redirectError,
              );
            });
            if (this.abortController) {
              this.abortController.abort();
            }
            throw error;
          }

          // Don't log connection errors to Sentry - they're expected and frequent
          // Use breadcrumbs for debugging without consuming quota
          logger.addBreadcrumb({
            message: "SSE connection error",
            category: "sse",
            level: "warning",
            data: {
              error: message,
              status: maybeStatus,
            },
          });

          // Log as warning locally for debugging
          logger.warn("❌ SSE connection error:", { 
            error: message, 
            status: maybeStatus,
            url: finalUrl.toString()
          });
          this.setState("error");
        },
      });
    } catch (error) {
      // Don't log SSE connection creation errors to Sentry
      // These are frequent and expected network issues
      logger.warn("Failed to create SSE connection:", {
        error: error instanceof Error ? error.message : String(error)
      });
      this.setState("error");
    }
  }

  /**
   * Process an SSE event through the standard pipeline: dedup, buffer, notify listeners, broadcast.
   * Public to allow SSERecorderPlayback to inject recorded events during playback (AIE-295).
   */
  public processEvent(event: SSEEvent) {
    // Update last event ID for resume capability
    if (!isHeartbeatEvent(event)) {
      this.lastEventId = event.id;
    }

    // Add to processed events set (with size limit)
    this.processedEventIds.add(event.id);
    if (this.processedEventIds.size > 1000) {
      // Remove oldest entries
      const entries = Array.from(this.processedEventIds);
      entries.slice(0, 500).forEach((id) => this.processedEventIds.delete(id));
    }

    // Add to event buffer
    this.eventBuffer.push(event);
    if (this.eventBuffer.length > this.config.eventBufferSize) {
      this.eventBuffer.shift();
    }

    // Notify local listeners
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        logger.error("Error in SSE event listener:", error);
      }
    });

    // Broadcast to other tabs
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage({
        type: "sse_event",
        data: event,
      });
    }

    logger.debug(`📥 Processed SSE event: ${event.event} (${event.id})`);
  }

  /**
   * Clear the deduplication set. Called before playback so replayed events
   * (which carry their original IDs) aren't rejected. Safe because live SSE
   * is disconnected during playback (AIE-295).
   */
  public clearProcessedEventIds(): void {
    this.processedEventIds.clear();
  }

  private handleEventFromBroadcast(event: SSEEvent) {
    // Only process broadcast events if we're not the leader
    if (!this.isLeaderTab) {
      this.listeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          logger.error("Error in broadcast SSE event listener:", error);
        }
      });
    }
  }

  private startHeartbeatTimer() {
    this.heartbeatTimer = window.setTimeout(() => {
      logger.warn("⚠️ SSE heartbeat timeout - reconnecting");
      this.setState("reconnecting");
      // Abort current connection to trigger reconnect
      if (this.abortController) {
        this.abortController.abort();
      }
      // Only reconnect if we're still the leader
      if (this.isLeaderTab) {
        setTimeout(() => this.connect(this.authToken), 1000);
      }
    }, this.config.heartbeatTimeout);
  }

  private startTokenRefreshTimer() {
    // Clear existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    // Set up token refresh timer (every 30 minutes)
    this.tokenRefreshTimer = window.setTimeout(async () => {
      if (!this.isLeaderTab) return;

      try {
        logger.info("Refreshing SSE connection with new token");
        const freshToken = await this.getFreshToken();

        if (!freshToken) {
          logger.warn("Token refresh failed - keeping existing SSE connection");
          this.startTokenRefreshTimer();
          return;
        }

        if (freshToken === this.authToken) {
          logger.info("SSE token unchanged - skipping reconnect");
          this.startTokenRefreshTimer();
          return;
        }

        // Update stored token
        this.authToken = freshToken;
        
        // Reconnect with fresh token
        this.disconnect();
        setTimeout(() => {
          if (this.isLeaderTab) {
            this.connect(freshToken);
          }
        }, 1000);
      } catch (error) {
        logger.warn("Error during SSE token refresh:", error);
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  private resetHeartbeatTimer() {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.startHeartbeatTimer();
    }
  }

  private clearTimers() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    if (this.leaderElectionTimer) {
      clearTimeout(this.leaderElectionTimer);
      this.leaderElectionTimer = null;
    }
  }

  public disconnect() {
    this.clearTimers();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.setState("disconnected");
  }

  public addListener(
    listener: (event: SSEEvent) => void,
    options?: { replayBuffer?: boolean }
  ) {
    this.listeners.add(listener);

    // Only replay buffered events when explicitly requested (e.g., after reconnection).
    // Normal component mounts should NOT replay the buffer to avoid hammering
    // the React Query cache with stale events during navigation.
    if (options?.replayBuffer) {
      this.eventBuffer.forEach((event) => {
        try {
          listener(event);
        } catch (error) {
          logger.error("Error replaying buffered event:", error);
        }
      });
    }
  }

  public removeListener(listener: (event: SSEEvent) => void) {
    this.listeners.delete(listener);
  }

  public addStateListener(listener: (state: SSEConnectionState) => void) {
    this.stateListeners.add(listener);
    // Immediately notify of current state
    listener(this.currentState);
    this.requestLeaderState();
  }

  public removeStateListener(listener: (state: SSEConnectionState) => void) {
    this.stateListeners.delete(listener);
  }

  public getState(): SSEConnectionState {
    return this.currentState;
  }

  public getLastEventId(): string | null {
    return this.lastEventId;
  }

  /**
   * Restore the last-event-id cursor. Used by SSERecorderPlayback to undo
   * the cursor pollution caused by replaying recorded events (AIE-295).
   */
  public setLastEventId(id: string | null): void {
    this.lastEventId = id;
  }

  /**
   * Whether this tab is the SSE leader (owns the EventSource connection).
   * Playback should only run on the leader tab (AIE-295).
   */
  public getIsLeaderTab(): boolean {
    return this.isLeaderTab;
  }

  public configure(config: Partial<SSEConfig>) {
    this.config = { ...this.config, ...config };
  }
}

// React Hook Interface
interface UseModernSSEOptions {
  enabled?: boolean;
  token?: string;
  onEvent?: (event: SSEEvent) => void;
  config?: Partial<SSEConfig>;
  pollInterval?: number;
}

export function useModernSSE(options: UseModernSSEOptions = {}) {
  const { enabled = true, token, onEvent, config, pollInterval } = options;

  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] =
    useState<SSEConnectionState>("disconnected");
  const [lastEventId, setLastEventId] = useState<string | null>(null);
  const managerRef = useRef<SSEConnectionManager>();

  // Get singleton manager
  if (!managerRef.current) {
    managerRef.current = SSEConnectionManager.getInstance();
  }

  // Handle incoming events
  const handleEvent = useCallback(
    (event: SSEEvent) => {
      const isStudyEvent =
        event.event === "study.status" ||
        event.event === "trial.completed" ||
        event.event === "study.updated";
      const p = (event.data?.payload ?? {}) as Record<string, unknown>;

      // Update query cache based on event type
      if (isStudyEvent && event.study_id) {
        const entries = queryClient.getQueriesData<StudiesListResponse>({
          queryKey: ["studies"],
        });

        for (const [key, prev] of entries) {
          if (!prev) continue;

          // Fallback: plain Study[] shape
          if (Array.isArray(prev)) {
            const idx = prev.findIndex(
              (s: Study) => s.study_id === event.study_id,
            );
            if (idx >= 0) {
              const next = [...prev];
              const updated: Partial<Study> = { ...next[idx] };
              if (p.status) updated.status = p.status as Study['status'];
              if (typeof p.progress_percentage === "number") {
                updated.progress = p.progress_percentage;
                if (typeof p.current_trial === "number") {
                  updated.n_trials_completed = p.current_trial;
                  updated.current_trial = p.current_trial;
                }
              }
              // Monotonic guard: best_value is always a maximization metric
              // (Sortino/Sharpe). A decrease is stale backend data. (AIE-289)
              if (p.best_value != null) {
                const incoming = p.best_value as number;
                const current = (updated as Study).best_value;
                if (current == null || incoming > current) {
                  updated.best_value = incoming;
                }
              }
              next[idx] = updated as Study;
              queryClient.setQueryData(key as readonly unknown[], next);
            }
            continue;
          }

          // listStudies() returns { studies: Study[], total: number }
          const prevResponse = prev as StudiesListResponse;
          if (Array.isArray(prevResponse.studies)) {
            const idx = prevResponse.studies.findIndex(
              (s: Study) => s.study_id === event.study_id,
            );
            if (idx >= 0) {
              const next = { ...prevResponse, studies: [...prevResponse.studies] };
              const updated: Partial<Study> = { ...next.studies[idx] };
              if (p.status) updated.status = p.status as Study['status'];
              if (typeof p.progress_percentage === "number") {
                updated.progress = p.progress_percentage;
                if (typeof p.current_trial === "number") {
                  updated.n_trials_completed = p.current_trial;
                  updated.current_trial = p.current_trial;
                }
              }
              // Monotonic guard: best_value is always a maximization metric
              // (Sortino/Sharpe). A decrease is stale backend data. (AIE-289)
              if (p.best_value != null) {
                const incoming = p.best_value as number;
                const current = (updated as Study).best_value;
                if (current == null || incoming > current) {
                  updated.best_value = incoming;
                }
              }
              next.studies[idx] = updated as Study;
              queryClient.setQueryData(key as readonly unknown[], next);
            }
          }
        }

        // Only invalidate when the event represents a material change that
        // warrants a server refresh. This avoids spamming fetches on every
        // incremental progress update.
        let shouldRefetch = false;
        if (event.event === "study.status") {
          const status = typeof p.status === "string" ? p.status : undefined;
          // Include all terminal/lifecycle statuses that affect parallel count or study visibility
          // - completed/failed/ready: normal completion states
          // - deleted/cancelled/stopped/paused: user-initiated state changes that free slots
          shouldRefetch = Boolean(
            status &&
              ["completed", "failed", "ready", "deleted", "cancelled", "stopped", "paused"].includes(
                status
              )
          );

          // AIE-312: Patch cached study with completion data immediately
          // so Duration and Completed columns render without waiting for refetch
          if (status === "completed" && p && typeof p === "object" && "completion_summary" in p) {
            const completionSummary = (p as unknown as StudyStatusPayload).completion_summary;
            if (completionSummary) {
              const patchStudy = (s: Study): Study =>
                s.study_id === event.study_id
                  ? {
                      ...s,
                      status: "completed",
                      n_trials_completed: completionSummary.n_trials_completed ?? s.n_trials_completed,
                      current_trial: completionSummary.n_trials_completed ?? s.current_trial,
                      n_trials: completionSummary.n_trials ?? s.n_trials,
                      // Monotonic guard: best_value only increases (AIE-289)
                      best_value: (completionSummary.best_value != null && (s.best_value == null || completionSummary.best_value > s.best_value))
                        ? completionSummary.best_value
                        : s.best_value,
                      completed_at: completionSummary.completed_at ?? s.completed_at,
                      trials_per_second: completionSummary.trials_per_second ?? s.trials_per_second,
                      optimization_duration_seconds: completionSummary.optimization_duration_seconds ?? s.optimization_duration_seconds,
                    }
                  : s;

              queryClient.setQueriesData({ queryKey: ["studies"] }, (old: unknown) => {
                if (Array.isArray(old)) {
                  return old.map(patchStudy);
                }
                if (old && typeof old === "object" && "studies" in old && Array.isArray((old as Record<string, unknown>).studies)) {
                  return { ...old, studies: ((old as Record<string, unknown>).studies as Study[]).map(patchStudy) };
                }
                return old;
              });
            }
          }
        } else if (event.event === "study.updated") {
          // Updated payloads typically carry coarse-grained changes (e.g. metadata).
          shouldRefetch = true;
        }

        if (shouldRefetch) {
          queryClient.invalidateQueries({
            queryKey: ["studies"],
            refetchType: "active",
          });
          if (event.study_id) {
            queryClient.invalidateQueries({
              queryKey: ["study", event.study_id],
            });
          }
        }
      }

      // Call user-provided event handler
      if (onEvent) {
        onEvent(event);
      }

      // Update last event ID
      if (!isHeartbeatEvent(event)) {
        setLastEventId(event.id);
      }
    },
    [queryClient, onEvent],
  );

  // Setup connection and listeners
  useEffect(() => {
    const manager = managerRef.current!;

    // Configure if options provided
    if (config) {
      manager.configure(config);
    }

    if (token) {
      manager.setAuthToken(token);
    }

    // Add listeners
    manager.addListener(handleEvent);
    manager.addStateListener(setConnectionState);

    // Only connect if we're not already trying/connected and enabled
    if (enabled) {
      const state = manager.getState();
      const shouldConnect = state === "disconnected" || state === "error"; // avoid resetting healthy streams
      if (shouldConnect) {
        manager.connect(token);
      }
    }

    return () => {
      manager.removeListener(handleEvent);
      manager.removeStateListener(setConnectionState);
    };
  }, [enabled, token, config, handleEvent]);

  // Polling fallback for browsers with SSE issues (e.g., Edge)
  useEffect(() => {
    if (enabled && pollInterval && pollInterval > 0) {
      const intervalId = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ["studies"] });
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
    return undefined;
  }, [enabled, pollInterval, queryClient]);

  // Connection controls
  const connect = useCallback(() => {
    managerRef.current?.connect(token);
  }, [token]);

  const disconnect = useCallback(() => {
    managerRef.current?.disconnect();
  }, []);

  return {
    connectionState,
    lastEventId,
    connect,
    disconnect,
    isConnected: connectionState === "connected",
    isConnecting:
      connectionState === "connecting" || connectionState === "reconnecting",
    hasError: connectionState === "error",
  };
}
